import { useMemo, useState, useEffect, useCallback } from 'react';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { InspectionTrackerFormModal, CompletionDialog } from './InspectionTrackerFormModal';
import { getInspectionById, removeInspectionLine } from './FormikHelpers/Api';
import './inspection-tracker.scss';
import './tracker-status.scss';

const fmtDay = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—');
const fmtFull = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' }) : '—');

// What each occurrence state looks like. The word is always shown or spoken, never colour alone.
const STATE = {
  completed: { icon: 'check-circle', word: 'Completed' },
  skipped: { icon: 'skip-forward', word: 'Skipped' },
  overdue: { icon: 'alert-circle', word: 'Overdue' },
  due_soon: { icon: 'clock', word: 'Due soon' },
  upcoming: { icon: 'calendar', word: 'Upcoming' },
};

const ROW_STATUS = {
  overdue: { icon: 'alert-circle', word: 'Overdue' },
  due_soon: { icon: 'clock', word: 'Due soon' },
  done: { icon: 'check-circle', word: 'Up to date' },
  none: { icon: 'calendar', word: 'Nothing due yet' },
};

const relativeDue = (days) => {
  if (typeof days !== 'number') return '';
  if (days === 0) return 'today';
  if (days < 0) return `${Math.abs(days)} day${days === -1 ? '' : 's'} late`;
  return `in ${days} day${days === 1 ? '' : 's'}`;
};

const chipNote = (occ) => {
  if (occ.state === 'completed') return occ.completedDate ? `done ${fmtDay(occ.completedDate)}` : 'done';
  if (occ.state === 'upcoming') return '';
  return STATE[occ.state].word.toLowerCase();
};

const chipLabel = (occ) => {
  const parts = [fmtFull(occ.dueDate), STATE[occ.state].word];
  if (occ.state === 'completed' && occ.completedDate) parts.push(`on ${fmtFull(occ.completedDate)}`);
  if (occ.completedByName) parts.push(`by ${occ.completedByName}`);
  if (occ.state === 'skipped' && occ.skipReason) parts.push(occ.skipReason);
  return parts.join(', ');
};

const OccurrenceChip = ({ occ, onClick, disabled }) => (
  <Tooltip title={chipLabel(occ)} arrow>
    <span>
      <button type="button" className={`ts-chip ts-chip--${occ.state}`} onClick={onClick} disabled={disabled} aria-label={chipLabel(occ)}>
        <FeatherIcon icon={STATE[occ.state].icon} size={13} />
        {fmtDay(occ.dueDate)}
        {chipNote(occ) && <span className="ts-chip__note">· {chipNote(occ)}</span>}
      </button>
    </span>
  </Tooltip>
);

const InspectionTrackerListTable = ({
  rows = [],
  year,
  properties = [],
  filters = {},
  onSaveRow,
  onRefresh,
  userRole,
  userLead = false,
  typeOptions = [],
  frequencyOptions = [],
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isNewModal, setIsNewModal] = useState(false);
  const [focus, setFocus] = useState({ index: null, history: false });
  const [completing, setCompleting] = useState(null);
  const [busyKey, setBusyKey] = useState('');

  const role = String(userRole || '').toLowerCase();
  const canEdit = role === 'company' || role === 'auditor' || (role === 'technical staff' && !!userLead);
  const canAct = canEdit || role === 'manager' || role === 'technical staff';
  const singleBuilding = !!filters.property_id && !!filters.building_name;

  useEffect(() => {
    setModalOpen(false);
    setCompleting(null);
  }, [filters.property_id, filters.building_name]);

  const propertyContext = useMemo(() => {
    const prop = properties.find((p) => p._id === filters.property_id);
    return {
      propertyId: filters.property_id || '',
      buildingName: filters.building_name || '',
      name: prop?.title || prop?.name || '',
      type: filters.property_type || '',
    };
  }, [properties, filters.property_id, filters.building_name, filters.property_type]);

  const counts = useMemo(() => {
    const c = { overdue: 0, due_soon: 0, done: 0, none: 0 };
    rows.forEach((r) => { c[r.rowStatus] = (c[r.rowStatus] || 0) + 1; });
    return c;
  }, [rows]);

  const openRecord = useCallback(async (row, history = false) => {
    setBusyKey(row.key);
    try {
      const record = await getInspectionById(row.trackerId);
      if (!record) return;
      setModalData(record);
      setIsNewModal(false);
      setFocus({ index: row.itemIndex, history });
      setModalOpen(true);
    } catch {
      // the Api layer has already shown the server's message
    } finally {
      setBusyKey('');
    }
  }, []);

  const openNew = () => {
    setModalData(null);
    setIsNewModal(true);
    setFocus({ index: null, history: false });
    setModalOpen(true);
  };

  const handleModalSave = useCallback(async (payload, isNew) => {
    const result = await onSaveRow({ ...payload, _id: isNew ? undefined : modalData?._id });
    if (result === null) return null;
    return result?.data || result;
  }, [onSaveRow, modalData]);

  const removeLine = async (row) => {
    const answer = await Swal.fire({
      title: `Remove ${row.inspectionTypeName}?`,
      text: 'The schedule for this inspection is removed from the building. Its history is kept.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#DC2626',
      confirmButtonText: 'Remove',
      reverseButtons: true,
    });
    if (!answer.isConfirmed) return;
    setBusyKey(row.key);
    try {
      const out = await removeInspectionLine(row.trackerId, row.itemIndex);
      toast.success(out?.message || 'Removed');
      onRefresh?.();
    } catch {
      // toast already shown
    } finally {
      setBusyKey('');
    }
  };

  // The item the completion dialog works on. A closed block belongs to the last
  // cycle, so the dialog starts from nothing and opens the next cycle on save.
  const completingItem = completing
    ? {
      inspectionTypeName: completing.inspectionTypeName,
      contractor: completing.contractor,
      completed: completing.blockClosed ? null : completing.openBlock,
    }
    : null;

  const headerTitle = propertyContext.name
    ? `${propertyContext.name}${propertyContext.buildingName ? ` — ${propertyContext.buildingName}` : ''}${propertyContext.type ? ` (${propertyContext.type})` : ''}`
    : 'Select a Property to Begin';

  const columnCount = (singleBuilding ? 0 : 1) + 6;

  return (
    <Paper elevation={0} className="it-wrap it-paper">
      <div className="it-header">
        <div className="it-header__left">
          <div className="it-header__icon-wrap"><FeatherIcon icon="clipboard" size={18} color="#A7F3D0" /></div>
          <div className="it-header__meta">
            <div className="it-header__label">Inspection Tracker · {year}</div>
            <div className="it-header__title">{headerTitle}</div>
          </div>
          <div className="it-header__badges">
            {['overdue', 'due_soon', 'done', 'none'].filter((s) => counts[s] > 0).map((s) => (
              <span key={s} className={`ts-status-pill ts-status-pill--${s}`}>
                <FeatherIcon icon={ROW_STATUS[s].icon} size={12} /> {counts[s]} {ROW_STATUS[s].word.toLowerCase()}
              </span>
            ))}
          </div>
        </div>
        <div className="it-header__actions">
          {canEdit && singleBuilding && (
            <button type="button" className="it-btn it-btn--primary-solid it-add" onClick={openNew}>
              <FeatherIcon icon="plus" size={14} /> Add Inspection
            </button>
          )}
        </div>
      </div>

      <Box sx={{ maxHeight: '72vh', overflow: 'auto', borderRadius: '0 0 10px 10px' }} className="it-scroll-box">
        <table className="ts-table">
          <thead>
            <tr>
              {!singleBuilding && <th scope="col">Building</th>}
              <th scope="col">Inspection</th>
              <th scope="col">Frequency</th>
              <th scope="col">Due dates {year}</th>
              <th scope="col">Next due</th>
              <th scope="col">Files</th>
              <th scope="col"><span className="visually-hidden">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={columnCount} className="it-empty-cell">
                  <div className="it-empty">
                    <div className="it-empty__icon-wrap"><FeatherIcon icon="clipboard" size={26} color="#1A6B4A" /></div>
                    <Typography className="it-empty__title">{filters.property_id ? `Nothing due in ${year}` : 'No property selected'}</Typography>
                    <Typography className="it-empty__sub">
                      {!filters.property_id ? 'Select a property above to see its inspections.'
                        : !singleBuilding ? 'Select a building to add inspections, or pick another year.'
                          : canEdit ? 'Click "Add Inspection" to set one up, or pick another year.' : 'Pick another year, or clear the filters.'}
                    </Typography>
                  </div>
                </td>
              </tr>
            )}

            {rows.map((row) => {
              const busy = busyKey === row.key;
              const due = row.nextDue;
              const nextTone = due && due.daysUntilDue < 0 ? 'overdue' : row.rowStatus === 'due_soon' ? 'due_soon' : '';
              return (
                <tr key={row.key} className={`ts-row ts-row--${row.rowStatus}`} aria-busy={busy}>
                  {!singleBuilding && <td><div className="ts-primary">{row.buildingName}</div></td>}
                  <td>
                    <div className="ts-primary">{row.inspectionTypeName}</div>
                    {row.contractor && <div className="ts-secondary">{row.contractor}</div>}
                  </td>
                  <td>{row.frequencyName}</td>
                  <td>
                    <div className="ts-chips">
                      {row.occurrences.map((occ) => (
                        <OccurrenceChip
                          key={occ.dueDate}
                          occ={occ}
                          disabled={busy}
                          onClick={() => (occ.isOpen && canAct ? setCompleting(row) : openRecord(row, occ.state === 'completed' || occ.state === 'skipped'))}
                        />
                      ))}
                    </div>
                  </td>
                  <td>
                    {due ? (
                      <>
                        <div className={`ts-next${nextTone ? ` ts-next--${nextTone}` : ''}`}>{fmtFull(due.date)}</div>
                        <div className="ts-secondary">{relativeDue(due.daysUntilDue)}</div>
                      </>
                    ) : '—'}
                  </td>
                  <td>
                    {row.photos + row.invoices > 0 ? (
                      <span className="ts-secondary" title={`${row.photos} photo${row.photos === 1 ? '' : 's'}, ${row.invoices} invoice${row.invoices === 1 ? '' : 's'}`}>
                        <FeatherIcon icon="paperclip" size={12} /> {row.photos + row.invoices}
                      </span>
                    ) : <span className="ts-secondary">—</span>}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      {canAct && (
                        <button type="button" className="ts-icon-btn ts-icon-btn--primary" onClick={() => setCompleting(row)} disabled={busy} aria-label={`Record completion of ${row.inspectionTypeName} due ${fmtFull(due?.date)}`}>
                          <FeatherIcon icon="check" size={14} /> Complete
                        </button>
                      )}
                      <Tooltip title={canEdit ? 'Open: edit, skip, history' : 'Open: skip, notes, history'} arrow>
                        <span>
                          <button type="button" className="ts-icon-btn" onClick={() => openRecord(row)} disabled={busy} aria-label={`Open ${row.inspectionTypeName}`}>
                            <FeatherIcon icon={canEdit ? 'edit-2' : 'eye'} size={14} />
                          </button>
                        </span>
                      </Tooltip>
                      {canEdit && (
                        <Tooltip title="Remove this inspection" arrow>
                          <span>
                            <button type="button" className="ts-icon-btn ts-icon-btn--danger" onClick={() => removeLine(row)} disabled={busy} aria-label={`Remove ${row.inspectionTypeName}`}>
                              <FeatherIcon icon="trash-2" size={14} />
                            </button>
                          </span>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Box>

      {completing && (
        <CompletionDialog
          isOpen
          onClose={() => setCompleting(null)}
          item={completingItem}
          index={completing.itemIndex}
          inspectionId={completing.trackerId}
          startNewCycle={completing.blockClosed}
          dueDate={completing.nextDue?.date}
          onRecordUpdated={() => onRefresh?.()}
        />
      )}

      <InspectionTrackerFormModal
        isOpen={modalOpen}
        onClose={(changed) => { setModalOpen(false); if (changed && onRefresh) onRefresh(); }}
        onSave={handleModalSave}
        inspectionData={isNewModal ? null : modalData}
        isNew={isNewModal}
        focusIndex={focus.index}
        focusHistory={focus.history}
        propertyContext={propertyContext}
        typeOptions={typeOptions}
        frequencyOptions={frequencyOptions}
        userRole={userRole}
        userLead={userLead}
      />
    </Paper>
  );
};

export { InspectionTrackerListTable };
